/**
 * useBullhornNoteHandler — single entry-point for post-send Bullhorn
 * note logic. All three call-sites (Documents-Send, Packet-Send,
 * Form-Send) use this hook so the admin's note-mode setting is the
 * ONLY place that controls behaviour.
 *
 * Modes (from tenant.settings.bullhorn_note_mode):
 *   popup  → render BullhornNotePromptDialog (current behaviour)
 *   toast  → silently POST /api/bullhorn/notes; on success show toast
 *            "Note logged to Bullhorn"; on failure fall back to popup
 *   silent → silently POST; on success no UI; on failure fall back to popup
 *   off    → no note, no popup, no toast
 *
 * Two execution contexts:
 *   - Main app pages (SendDocumentPage) — pass nothing; the hook reads
 *     ``tenant.settings`` from AuthContext.
 *   - Bullhorn iframe (BullhornEmbedPage) — pass the ``embedApi`` axios
 *     instance; the hook fetches ``/api/auth/me`` through it on mount
 *     to pick up the same tenant settings the main app uses.
 */
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOKEN_RE = /\{(template_name|form_name|recipient_name|recipient_email|sender_name|document_type)\}/g;

/** Replace ``{token}`` placeholders from a key→value map. */
function renderTemplate(template, context) {
  if (!template) return '';
  return template.replace(TOKEN_RE, (_, key) => {
    const v = context?.[key];
    return v == null ? '' : String(v);
  });
}

const DEFAULTS = {
  bullhorn_note_mode: 'popup',
  bullhorn_note_default_body:
    'Sent "{document_type}" via HyreDocs to {recipient_name} <{recipient_email}>.',
  bullhorn_note_default_action: 'General',
};

export function useBullhornNoteHandler(apiClient) {
  // AuthContext is always mounted (App.js wraps everything in AuthProvider)
  // so this is safe in BOTH the main app and the embed iframe. In the
  // embed the cookie-based session isn't set, so `tenant` will be null
  // and we fall back to fetching settings via the embed's axios below.
  const auth = useAuth();
  const authTenantSettings = auth?.tenant?.settings || null;

  const [fetchedSettings, setFetchedSettings] = useState(null);
  const fetchedOnce = useRef(false);

  // Make settings load callable on-demand. The embed page mounts the
  // auth bearer token in an effect that may race the initial fetch
  // here — if the first call goes out before `Authorization:` is
  // attached, it 401s, `fetchedSettings` stays null, and `mode`
  // silently falls back to the 'popup' default. Calling fire() then
  // pops the dialog even though tenant settings say 'toast'.
  const loadSettings = useCallback(async () => {
    if (authTenantSettings) return authTenantSettings;
    if (!apiClient) return null;
    try {
      const res = await apiClient.get('/api/tenants/current');
      const s = res?.data?.settings;
      if (s) {
        setFetchedSettings(s);
        return s;
      }
    } catch {
      /* swallow — caller falls back to defaults */
    }
    return null;
  }, [apiClient, authTenantSettings]);

  // Embed-context fallback: pull tenant settings via the embed axios
  // on first render so the admin's note-mode applies inside the iframe
  // too. Only fires when AuthContext didn't already provide them.
  useEffect(() => {
    if (authTenantSettings || fetchedOnce.current) return;
    if (!apiClient) return;
    fetchedOnce.current = true;
    loadSettings();
  }, [apiClient, authTenantSettings, loadSettings]);

  const settings = authTenantSettings || fetchedSettings || {};
  const mode = settings.bullhorn_note_mode || DEFAULTS.bullhorn_note_mode;
  const defaultBody =
    settings.bullhorn_note_default_body || DEFAULTS.bullhorn_note_default_body;
  const defaultAction =
    settings.bullhorn_note_default_action || DEFAULTS.bullhorn_note_default_action;

  const client = apiClient || axios;
  const url = useCallback(
    (path) => (apiClient ? path : `${API_URL}${path}`),
    [apiClient],
  );

  const [popupState, setPopupState] = useState({ open: false, props: null, onComplete: null });
  const closePopup = useCallback(() => {
    setPopupState((prev) => {
      // Fire the caller's onComplete (e.g. "navigate to /documents") on
      // close regardless of whether the recruiter clicked Save or Skip.
      if (typeof prev.onComplete === 'function') {
        try { prev.onComplete(); } catch { /* swallow */ }
      }
      return { open: false, props: null, onComplete: null };
    });
  }, []);

  const fire = useCallback(
    async ({ entityType, entityId, entityName, documentId, context, onComplete }) => {
      // Late-load settings if the first useEffect ran before the embed
      // attached its bearer token. Without this, the embed always
      // pops the popup even when the admin picked Auto-log toast.
      let effSettings = authTenantSettings || fetchedSettings;
      if (!effSettings) {
        effSettings = await loadSettings();
      }
      const eff = effSettings || {};
      const effMode = eff.bullhorn_note_mode || DEFAULTS.bullhorn_note_mode;
      const effBody = eff.bullhorn_note_default_body || DEFAULTS.bullhorn_note_default_body;
      const effAction = eff.bullhorn_note_default_action || DEFAULTS.bullhorn_note_default_action;

      if (effMode === 'off') {
        if (typeof onComplete === 'function') onComplete();
        return;
      }

      const body = renderTemplate(effBody, context).trim();

      if (effMode === 'popup') {
        setPopupState({
          open: true,
          props: {
            entityType, entityId, entityName, documentId,
            defaultAction: effAction, defaultComments: body,
          },
          onComplete: onComplete || null,
        });
        return;
      }

      try {
        await client.post(url('/api/bullhorn/notes'), {
          entity_type: entityType,
          entity_id: entityId,
          action: effAction,
          comments: body,
          document_id: documentId || null,
        });
        if (effMode === 'toast') {
          toast.success(`Note logged to Bullhorn for ${entityName || entityType}`);
        }
        if (typeof onComplete === 'function') onComplete();
      } catch (err) {
        // Graceful degradation — surface the popup so the recruiter can
        // retry/edit instead of silently losing the note.
        const detail = getErrorMessage(err, 'Auto-note failed');
        toast.error(`${detail} — opening manual prompt`);
        setPopupState({
          open: true,
          props: {
            entityType, entityId, entityName, documentId,
            defaultAction: effAction, defaultComments: body,
          },
          onComplete: onComplete || null,
        });
      }
    },
    [client, url, authTenantSettings, fetchedSettings, loadSettings],
  );

  return useMemo(
    () => ({
      mode,
      fire,
      popup: {
        open: popupState.open,
        onClose: closePopup,
        ...(popupState.props || {}),
      },
    }),
    [mode, fire, popupState, closePopup],
  );
}
